import React, { useState, useEffect, useCallback } from 'react';

/**
 * FileTree - Displays project files in a collapsible tree structure
 * Handles nested file structure from API (with children property)
 */
function FileTree({ files = [], onFileSelect, selectedFile, projectId }) {
  const storageKey = projectId ? `file-tree-expanded-${projectId}` : null;

  const [expandedFolders, setExpandedFolders] = useState(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) return new Set(JSON.parse(saved));
      } catch { /* ignore corrupt data */ }
    }
    return new Set(['src']);
  });

  // Persist expansion state to localStorage
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify([...expandedFolders]));
    }
  }, [expandedFolders, storageKey]);

  const toggleFolder = useCallback((path, e) => {
    e.stopPropagation();
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const iconMap = {
      'js': '📜',
      'jsx': '⚛️',
      'ts': '📘',
      'tsx': '⚛️',
      'css': '🎨',
      'scss': '🎨',
      'html': '🌐',
      'json': '📋',
      'md': '📝',
      'svg': '🖼️',
      'png': '🖼️',
      'jpg': '🖼️',
      'gif': '🖼️',
    };
    return iconMap[ext] || '📄';
  };

  const renderNode = (node, depth = 0) => {
    const isFolder = node.type === 'directory';
    const isExpanded = expandedFolders.has(node.path);
    const isSelected = selectedFile === node.path;
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.path} className="file-tree-node">
        <div
          className={`file-tree-item ${isSelected ? 'selected' : ''} ${isFolder ? 'folder' : 'file'}`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={(e) => {
            if (isFolder) {
              toggleFolder(node.path, e);
            } else {
              onFileSelect?.(node.path);
            }
          }}
        >
          {isFolder ? (
            <>
              <span className={`folder-arrow ${isExpanded ? 'expanded' : ''}`}>
                ▶
              </span>
              <span className="folder-icon">{isExpanded ? '📂' : '📁'}</span>
            </>
          ) : (
            <>
              <span className="file-indent"></span>
              <span className="file-icon">{getFileIcon(node.name)}</span>
            </>
          )}
          <span className="file-name">{node.name}</span>
        </div>
        
        {isFolder && isExpanded && hasChildren && (
          <div className="file-tree-children">
            {node.children
              .sort((a, b) => {
                // Folders first, then alphabetically
                if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
                return a.name.localeCompare(b.name);
              })
              .map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (files.length === 0) {
    return (
      <div className="file-tree-empty">
        <span>No files yet</span>
      </div>
    );
  }

  // Sort root level: folders first, then alphabetically
  const sortedFiles = [...files].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="file-tree">
      {sortedFiles.map(file => renderNode(file, 0))}
    </div>
  );
}

export default FileTree;
